import type { ApiClient } from '@/api/client.types';
import type { RawComponent } from '@/features/courses/useCourse';

export interface CopyComponentParams {
  component: RawComponent;
  toCourseId: string;
}

export interface DeleteComponentParams {
  componentId: string;
  componentUid?: number | string | null;
  fromCourseId: string;
  userId: string;
}

export interface CopyComponentRequest {
  component: RawComponent;
  component_uid?: number | string;
  to_course_id: string;
}

export interface DeleteComponentRequest {
  component_id: string;
  component_uid?: number | string;
  from_course_id: string;
  user_id: string;
}

function normalizeNumericId(value: number | string | null | undefined) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  if (!trimmed || !/^\d+$/.test(trimmed)) {
    return undefined;
  }

  return trimmed;
}

export function buildCopyComponentRequest({
  component,
  toCourseId,
}: CopyComponentParams): CopyComponentRequest {
  const componentUid = normalizeNumericId(component.uid);

  return {
    component,
    ...(componentUid !== undefined ? { component_uid: componentUid } : {}),
    to_course_id: toCourseId,
  };
}

export function buildDeleteComponentRequest({
  componentId,
  componentUid,
  fromCourseId,
  userId,
}: DeleteComponentParams): DeleteComponentRequest {
  const normalizedComponentUid = normalizeNumericId(componentUid);

  return {
    component_id: componentId,
    ...(normalizedComponentUid !== undefined
      ? { component_uid: normalizedComponentUid }
      : {}),
    from_course_id: fromCourseId,
    user_id: userId,
  };
}

export async function copyComponent(
  apiClient: Pick<ApiClient, 'invokeKuduMethod'>,
  params: CopyComponentParams,
): Promise<void> {
  await apiClient.invokeKuduMethod<Record<string, never>, CopyComponentRequest>(
    'CopyComponent',
    buildCopyComponentRequest(params),
  );
}

export async function deleteComponent(
  apiClient: Pick<ApiClient, 'invokeKuduMethod'>,
  params: DeleteComponentParams,
): Promise<void> {
  await apiClient.invokeKuduMethod<
    Record<string, never>,
    DeleteComponentRequest
  >('DeleteComponent', buildDeleteComponentRequest(params));
}
